<template>
  <div class="section">
    <div class="center-section">
      <div class="form-heading">
        <div class="header-1">
          <span v-if="isConfirmed">
            <BaseIcon
              iconName="check"
              color="#20AD8B"
              width="32"
              height="32"
              class="status-icon"
            />
            {{ $t('auth.verification.confirmed') }}
          </span>

          <span v-else-if="isError">
            <BaseIcon
              iconName="error"
              color="#ff6b6b"
              width="32"
              height="32"
              class="status-icon"
            />
            {{ errorMessage }}
          </span>
          <span v-else>
            <div class="loading-text">
              <BaseIcon iconName="rotate" class="rotating" width="32" height="32" />
              {{ $t('auth.verification.confirming') }}
            </div>
          </span>
        </div>
      </div>

      <div class="message-box" :class="{ success: isConfirmed, error: isError }">
        <p v-if="isConfirmed" class="caption">
          {{ $t('auth.verification.confirmedDescription') }}
        </p>
        <p v-else-if="isError" class="caption">
          {{ errorDetails }}
        </p>
      </div>

      <div class="action-link caption">
        <router-link v-if="isConfirmed" to="/login" class="link">
          <BaseIcon iconName="chevronRight" class="icon-inline" />
          {{ $t('auth.verification.continueToLogin') }}
        </router-link>
        <button
          v-else-if="isError && route.query.email"
          @click="resendVerificationLink"
          class="link button-link"
          :disabled="isResending"
        >
          <BaseIcon
            :iconName="isResending ? 'rotate' : 'refresh'"
            class="icon-inline"
            :class="{ rotating: isResending }"
          />
          {{ isResending ? $t('auth.verification.sending') : $t('auth.verification.requestNewLink') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import BaseIcon from "@/components/BaseComponents/BaseIcon.vue";
import { authAPI } from "@/services/api";
import { useI18n } from 'vue-i18n';

const isConfirmed = ref(false);
const isError = ref(false);
const errorMessage = ref("");
const errorDetails = ref("");
const route = useRoute();
const isResending = ref(false);
const router = useRouter();
const { t } = useI18n();

async function resendVerificationLink() {
  isResending.value = true;
  try {
    await authAPI.resendVerification(route.query.email);
    isError.value = false;
    router.push({
      path: "/signup/success",
      query: { email: route.query.email },
    });
  } catch (error) {
    console.error("Failed to resend verification:", error);
    errorDetails.value = t('auth.verification.failedToSendNewLink');
  } finally {
    isResending.value = false;
  }
}

onMounted(async () => {
  const token = route.params.token;

  if (!token) {
    isError.value = true;
    errorMessage.value = t('auth.verification.invalidLink');
    errorDetails.value =
      t('auth.verification.invalidLinkDescription');
    return;
  }

  try {
    const response = await authAPI.confirmEmail(token);

    if (response.status === 202) {
      isConfirmed.value = true;
    } else {
      throw response;
    }
  } catch (error) {
    isError.value = true;

    if (error.status === 404) {
      errorMessage.value = t('auth.verification.linkNotFound');
      errorDetails.value =
        t('auth.verification.linkNotFoundDescription');
    } else if (error.status === 406) {
      errorMessage.value = t('auth.verification.linkExpired');
      errorDetails.value =
        t('auth.verification.linkExpiredDescription');
    } else {
      errorMessage.value = t('auth.verification.failed');
      errorDetails.value =
        t('auth.verification.failedDescription');
    }
  }
});
</script>

<style scoped>
.section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-xl);
}

.center-section {
  width: 35%;
  min-width: 300px;
  text-align: center;
}

.form-heading {
  margin-bottom: var(--spacing-lg);
}

.form-heading .header-1 {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
}

.message-box {
  padding: var(--spacing-lg);
  background-color: var(--bg-card);
  border-radius: var(--border-radius-2xl);
  margin-bottom: var(--spacing-lg);
}

.message-box.success {
  background-color: var(--color-accent-primary-15);
}

.message-box.error {
  background-color: var(--color-cancel-15);
}

.message-box .caption {
  color: var(--color-white-70);
  line-height: var(--line-height-relaxed);
}

.action-link {
  text-align: center;
}

.link {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--color-white);
  text-decoration: none;
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-md);
  background-color: var(--color-white-10);
  transition: background-color var(--transition-fast);
}

.link:hover {
  background-color: var(--color-white-20);
}

.loading-text {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.icon-inline {
  width: 20px;
  height: 20px;
}

@media (max-width: 768px) {
  .center-section {
    width: 90%;
  }
}

.button-link {
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  background: none;
}

.button-link:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
